import { useCallback, useEffect, useMemo, useState } from "react";
import CrudPanel from "views/admin/management/components/CrudPanel";
import { academicService } from "services/academicService";
import { adminService } from "services/adminService";
import { useAppLanguage } from "hooks/useAppLanguage";
import { normalizeSelectOptions } from "lib/selectOptions";
import {
  SUBJECT_ICON_DEFAULT,
  getSubjectIconOption,
  subjectIconSelectOptions,
} from "constants/subjectIcons";
import SubjectIconPicker from "components/ui/SubjectIconPicker";

type TabKey = "stages" | "grades" | "subjects";

type GradeOptionRow = {
  id: number;
  name: string;
  stage_id: number;
};

const tabs: Array<{ key: TabKey; ar: string; en: string; hintAr: string; hintEn: string }> = [
  {
    key: "stages",
    ar: "المراحل الدراسية",
    en: "Study stages",
    hintAr: "عرّف المراحل التعليمية مرة واحدة.",
    hintEn: "Define the education stages once.",
  },
  {
    key: "grades",
    ar: "الصفوف",
    en: "Grades",
    hintAr: "أضف الصفوف داخل كل مرحلة.",
    hintEn: "Add grades inside each stage.",
  },
  {
    key: "subjects",
    ar: "المواد",
    en: "Subjects",
    hintAr: "يمكن ربط المادة بأكثر من مرحلة وصف.",
    hintEn: "A subject can be linked to multiple stages and grades.",
  },
];

export default function AcademicPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [activeTab, setActiveTab] = useState<TabKey>("stages");
  const [stageOptions, setStageOptions] = useState<{ value: number; label: string }[]>([]);
  const [gradeRows, setGradeRows] = useState<GradeOptionRow[]>([]);
  const [gradesLoading, setGradesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    academicService
      .listStages({ limit: 500, sort_by: "sort_order", sort_direction: "asc" })
      .then((result) => {
        if (!cancelled) {
          setStageOptions(
            normalizeSelectOptions(result?.data || [], (stage: { name: string }) => stage.name)
          );
        }
      })
      .catch(() => {
        if (!cancelled) setStageOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "subjects") {
      return;
    }

    let cancelled = false;
    setGradesLoading(true);

    academicService
      .listGrades({ limit: 500, sort_by: "sort_order", sort_direction: "asc" })
      .then((result) => {
        if (cancelled) return;
        const rows = (result?.data || []) as GradeOptionRow[];
        setGradeRows(
          rows.map((grade) => ({
            id: Number(grade.id),
            name: String(grade.name ?? ""),
            stage_id: Number(grade.stage_id),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setGradeRows([]);
      })
      .finally(() => {
        if (!cancelled) setGradesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const selectedTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const subjectCreateTemplate = useMemo(
    () => ({
      name: "",
      icon: SUBJECT_ICON_DEFAULT,
      stage_ids: [] as number[],
      grade_ids: [] as number[],
      sort_order: 0,
      is_active: true,
    }),
    []
  );
  const gradeCreateTemplate = useMemo(
    () => ({ name: "", stage_id: 0, sort_order: 0, is_active: true }),
    []
  );
  const stageCreateTemplate = useMemo(
    () => ({ name: "", sort_order: 0, is_active: true }),
    []
  );

  const subjectIconOptions = useMemo(
    () => subjectIconSelectOptions(isArabic),
    [isArabic]
  );

  const gradeOptions = useMemo(
    () =>
      gradeRows.map((grade) => ({
        value: grade.id,
        label: grade.name,
      })),
    [gradeRows]
  );

  const subjectFieldOptionsResolver = useCallback(
    (formState: Record<string, unknown>) => {
      const stageIds = Array.isArray(formState.stage_ids) ? formState.stage_ids.map(Number) : [];
      if (stageIds.length === 0) {
        return { grade_ids: [] as { value: number; label: string }[] };
      }

      return {
        grade_ids: gradeRows
          .filter((grade) => stageIds.includes(grade.stage_id))
          .map((grade) => ({
            value: grade.id,
            label: `${grade.name} — ${stageOptions.find((stage) => stage.value === grade.stage_id)?.label ?? ""}`,
          })),
      };
    },
    [gradeRows, stageOptions]
  );

  const subjectFieldEmptyHintsResolver = useCallback(
    (formState: Record<string, unknown>) => {
      const stageIds = Array.isArray(formState.stage_ids) ? formState.stage_ids.map(Number) : [];
      if (stageIds.length === 0) {
        return {
          grade_ids: {
            ar: "اختر مرحلة أو أكثر أولًا",
            en: "Select one or more stages first",
          },
        };
      }

      return {
        grade_ids: {
          ar: "لا توجد صفوف للمراحل المختارة",
          en: "No grades for the selected stages",
        },
      };
    },
    []
  );

  const onSubjectFormFieldChange = useCallback(
    (key: string, value: unknown, formState: Record<string, unknown>) => {
      if (key !== "stage_ids") {
        return {};
      }

      const nextStageIds = Array.isArray(value) ? value.map(Number) : [];
      const currentIds = Array.isArray(formState.grade_ids)
        ? (formState.grade_ids as unknown[]).map(Number)
        : [];

      const nextIds = currentIds.filter((id) =>
        gradeRows.some((grade) => grade.id === id && nextStageIds.includes(grade.stage_id))
      );

      return { grade_ids: nextIds };
    },
    [gradeRows]
  );

  const mapSubjectToForm = useCallback((entity: Record<string, unknown>) => {
    const fromIds = Array.isArray(entity.grade_ids)
      ? entity.grade_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    const fromGrades = Array.isArray(entity.grades)
      ? (entity.grades as Array<{ id?: number }>)
          .map((grade) => Number(grade?.id))
          .filter((id) => Number.isFinite(id) && id > 0)
      : [];
    const legacyId = Number(entity.grade_id);
    const grade_ids =
      fromIds.length > 0
        ? fromIds
        : fromGrades.length > 0
          ? fromGrades
          : Number.isFinite(legacyId) && legacyId > 0
            ? [legacyId]
            : [];

    return {
      name: entity.name ?? "",
      icon: typeof entity.icon === "string" && entity.icon ? entity.icon : SUBJECT_ICON_DEFAULT,
      stage_ids: Array.isArray(entity.stage_ids) && entity.stage_ids.length > 0
        ? entity.stage_ids.map(Number)
        : Number(entity.stage_id) > 0 ? [Number(entity.stage_id)] : [],
      grade_ids,
      sort_order: Number(entity.sort_order) || 0,
      is_active: entity.is_active !== false,
    };
  }, []);

  const commonSortOptions = [
    { value: "id", labelAr: "المعرّف", labelEn: "ID" },
    { value: "name", labelAr: "الاسم", labelEn: "Name" },
    { value: "sort_order", labelAr: "الترتيب", labelEn: "Order" },
    { value: "created_at", labelAr: "تاريخ الإنشاء", labelEn: "Created at" },
  ];

  return (
    <div
      className="min-h-full bg-[#0d1218] px-4 pb-10 pt-3 text-white md:px-8"
      dir={isArabic ? "rtl" : "ltr"}
    >
      <section className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-col gap-2">
          <span className="text-sm font-medium text-[#ff6a3d]">
            {isArabic ? "إدارة المنصة / الشؤون الأكاديمية" : "Platform / Academic affairs"}
          </span>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            {isArabic ? "المعلومات الأكاديمية" : "Academic information"}
          </h1>
          <p className="max-w-2xl text-sm leading-7 text-slate-400 md:text-base">
            {isArabic
              ? "نظام بسيط: مرحلة، ثم صفوف داخل المرحلة، ومواد يمكن ربطها بصف أو أكثر."
              : "A simple hierarchy: stage, grades inside the stage, and subjects linked to one or more grades."}
          </p>
        </div>

        <div className="mb-6 rounded-2xl border border-slate-700/80 bg-[#151d29] p-3 shadow-2xl shadow-black/20">
          <div className="mb-3 text-sm font-medium text-slate-300">
            {isArabic ? "المراحل الدراسية" : "Academic structure"}
          </div>
          <div className="flex flex-wrap gap-3">
            {tabs.map((tab) => {
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`min-w-[145px] rounded-2xl border px-5 py-3 text-sm font-semibold transition-all duration-200 md:text-base ${
                    active
                      ? "border-[#ff5a22] bg-[#201713] text-[#ff9b83] shadow-[0_0_0_1px_rgba(255,90,34,.16)]"
                      : "border-slate-600 bg-[#202a3a] text-slate-200 hover:border-slate-400 hover:bg-[#273449]"
                  }`}
                >
                  {isArabic ? tab.ar : tab.en}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-[#72cfff] md:text-2xl">
              {isArabic ? selectedTab.ar : selectedTab.en}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              {isArabic ? selectedTab.hintAr : selectedTab.hintEn}
            </p>
          </div>
          <span className="hidden rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs text-slate-400 md:inline-flex">
            {activeTab === "subjects"
              ? isArabic
                ? "الربط: مراحل وصفوف متعددة"
                : "Relation: multiple stages and grades"
              : isArabic
                ? "إدارة مستقلة"
                : "Independent management"}
          </span>
        </div>

        {activeTab === "stages" && (
          <CrudPanel
            title={isArabic ? "المراحل الدراسية" : "Study stages"}
            description={isArabic ? "مثل: ابتدائي، إعدادي، ثانوي." : "For example: primary, preparatory, secondary."}
            list={academicService.listStages}
            show={academicService.getStage}
            create={academicService.createStage}
            update={academicService.updateStage}
            remove={academicService.deleteStage}
            createTemplate={stageCreateTemplate}
            requiredFields={["name"]}
            previewKeys={["id", "name", "sort_order", "is_active"]}
            enableIsActiveFilter
            sortOptions={commonSortOptions}
          />
        )}

        {activeTab === "grades" && (
          <CrudPanel
            title={isArabic ? "الصفوف" : "Grades"}
            description={isArabic ? "كل صف يجب أن يتبع مرحلة واحدة." : "Every grade belongs to one stage."}
            list={academicService.listGrades}
            show={academicService.getGrade}
            create={academicService.createGrade}
            update={academicService.updateGrade}
            remove={academicService.deleteGrade}
            createTemplate={gradeCreateTemplate}
            fieldOptions={{ stage_id: stageOptions }}
            fieldPlaceholders={{ stage_id: { ar: "اختر المرحلة", en: "Select stage" } }}
            fieldEmptyHints={{ stage_id: { ar: "أضف مرحلة أولًا", en: "Add a stage first" } }}
            requiredFields={["name", "stage_id"]}
            previewKeys={["id", "name", "stage_id", "sort_order", "is_active"]}
            enableIsActiveFilter
            sortOptions={commonSortOptions}
          />
        )}

        {activeTab === "subjects" && (
          <CrudPanel
            title={isArabic ? "المواد" : "Subjects"}
            description={
              isArabic
                ? "اختر مرحلة أو أكثر، ثم اختر الصفوف التي ستظهر المادة لطلابها."
                : "Select one or more stages, then choose the grades whose students can see the subject."
            }
            list={adminService.getSubjects}
            show={adminService.getSubject}
            create={adminService.createSubject}
            update={adminService.updateSubject}
            remove={adminService.deleteSubject}
            createTemplate={subjectCreateTemplate}
            mapEntityToForm={mapSubjectToForm}
            fieldOptions={{ stage_ids: stageOptions, icon: subjectIconOptions }}
            fieldRenderers={{
              icon: ({ value, onChange, isArabic: ar, disabled }) => (
                <SubjectIconPicker
                  value={typeof value === "string" ? value : SUBJECT_ICON_DEFAULT}
                  onChange={(next) => onChange(next)}
                  isArabic={ar}
                  disabled={disabled}
                />
              ),
            }}
            previewFormatters={{
              stage_names: (row) => Array.isArray(row.stage_names)
                ? row.stage_names.join("، ")
                : String(row.stage_name ?? "—"),
              icon: (row) => {
                const option = getSubjectIconOption(
                  typeof row.icon === "string" ? row.icon : SUBJECT_ICON_DEFAULT
                );
                const Icon = option.Icon;
                const label = isArabic ? option.labelAr : option.labelEn;
                return (
                  <span className={`inline-flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${option.accent}22`, color: option.accent }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span>{label}</span>
                  </span>
                );
              },
            }}
            fieldOptionsResolver={subjectFieldOptionsResolver}
            fieldOptionsLoading={{ grade_ids: gradesLoading }}
            fieldPlaceholders={{
              icon: { ar: "اختر أيقونة المادة", en: "Select subject icon" },
              stage_ids: { ar: "اختر مرحلة أو أكثر", en: "Select one or more stages" },
              grade_ids: { ar: "اختر صفًا أو أكثر", en: "Select one or more grades" },
            }}
            fieldEmptyHints={{
              stage_ids: { ar: "أضف مرحلة أولًا", en: "Add a stage first" },
            }}
            fieldEmptyHintsResolver={subjectFieldEmptyHintsResolver}
            onFormFieldChange={onSubjectFormFieldChange}
            requiredFields={["name", "icon", "stage_ids", "grade_ids"]}
            previewKeys={["id", "name", "icon", "stage_names", "related_to", "sort_order", "is_active"]}
            enableIsActiveFilter
            sortOptions={commonSortOptions}
            filterFields={[
              {
                key: "filter[stage_id]",
                labelAr: "المرحلة",
                labelEn: "Stage",
                type: "select",
                options: stageOptions.map((option) => ({
                  value: String(option.value),
                  labelAr: option.label,
                  labelEn: option.label,
                })),
              },
              {
                key: "filter[grade_id]",
                labelAr: "الصف",
                labelEn: "Grade",
                type: "select",
                options: gradeOptions.map((option) => ({
                  value: String(option.value),
                  labelAr: option.label,
                  labelEn: option.label,
                })),
              },
            ]}
          />
        )}
      </section>
    </div>
  );
}
